import { useRoleGuard } from '@/hooks/useRoleGuard.jsx'
import { Children, cloneElement, isValidElement } from 'react'
import { TypographySmall } from '@/components/ui/typography.jsx'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

/**
 * RoleGuard - A component that restricts access based on user role
 */
export const RoleGuard = ({ children, minimumRoleRequired, level, hideContent = false, message = "Insufficient permission" }) => {
    // Check if the user has the required role
    const hasAccess = useRoleGuard(minimumRoleRequired, level)

    // If user has access, render the children
    if (hasAccess) {
        return children
    }

    // If user doesn't have access and hideContent is true, return null
    if (hideContent) {
        return null
    }

    // Clone and modify children to add event handlers - that way we prevent the user from interacting with the element even it it's alter the DOM manually
    const modifiedChildren = Children.map(children, (child) => {
        // Only process valid React elements
        if (!isValidElement(child)) {
            return child
        }

        // Prevent click events
        const preventClick = (e) => {
            e.preventDefault()
            e.stopPropagation()
            return false
        }

        // Clone the element with additional props
        return cloneElement(child, {
            onClick: preventClick,
            onMouseDown: preventClick,
            onMouseUp: preventClick,
            onTouchStart: preventClick,
            onTouchEnd: preventClick,
            onKeyDown: (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    preventClick(e)
                }
                // Call original onKeyDown if it exists
                if (child.props.onKeyDown) {
                    child.props.onKeyDown(e)
                }
            },
            // Disable the element
            disabled: true,
            // Add aria attributes for accessibility
            'aria-disabled': true,
            className: `${child.props.className || ''} opacity-50 pointer-events-none`
        })
    })

    // If user doesn't have access and hideContent is false, render with tooltip
    return (
        <TooltipProvider delayDuration={100}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div className="inline-block cursor-not-allowed">
                        {modifiedChildren}
                    </div>
                </TooltipTrigger>
                <TooltipContent>
                    <TypographySmall>
                        {message}
                    </TypographySmall>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    )
}
